import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { parseCsvDateToIso } from "../shared/utils/csvDate";
import GlobalTimeNavigatorBar from "../components/GlobalTimeNavigatorBar";
import OpenClosedFilterDropdown from "../components/OpenClosedFilterDropdown";
import EntityPageScaffold from "../components/entity/EntityPageScaffold";
import { TableActionColumnHeader } from "../components/TableShell";
import ImportCsvPanel from "../components/entity/ImportCsvPanel";
import CrudListToolbar from "../components/entity/CrudListToolbar";
import DuplicateSuccessMessage from "../components/DuplicateSuccessMessage";
import ProjectFromTemplateModal from "../components/ProjectFromTemplateModal";
import TabLabelWithCount from "../components/TabLabelWithCount";
import { useEntityListChrome } from "../shared/hooks/useEntityListChrome";
import { useGlobalTimeNavigator } from "../shared/hooks/useGlobalTimeNavigator";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";
import { bulkSequentialDelete } from "../shared/utils/bulkSequentialDelete";
import { formatBatchSummaryMessage } from "../shared/utils/batchSummaryMessage";
import { attachValidatedCsvFileImport, downloadCsvTextFile, downloadCsvTextFromString } from "../shared/utils/csvFileHelpers";
import { parseCsvTextToNormalizedTable, findCsvColumnIndex, csvCellAt } from "../shared/utils/csvParse";
import uiConfig from "../uiConfig";
import { getMessageForFetchError } from "../shared/utils/apiError";
import {
  createProjectFromTemplate,
  fetchDeleteProjectImpact,
  fetchSystemTemplateProjects,
  formatDeleteProjectImpactSummary,
} from "../shared/api/projects";
import { IconDuplicate, IconDelete, IconAiAddTasks, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import { useAuth } from "../contexts/AuthContext";
import { getAssetLabel } from "../shared/utils/assetLabel";
import { buildDuplicateLabel } from "../shared/utils/duplicateLabel";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import { formatDateCell } from "../shared/utils/dateDisplay";
import { navigateToAiProjectBuilderForExistingProject } from "../shared/utils/aiProjectBuilderNavigate";
import { getProjectProgressFillColor, getProjectProgressTooltipTitle } from "../shared/utils/projectProgressColor";
import dayjs from "dayjs";

import { apiFetch } from "../shared/api/apiFetch";

const PROJECT_CSV_HEADER = "asset,asset.category,project,project.start_date,project.end_date";
const PROJECT_CSV_COLUMN_HINT =
  "Columns: asset, asset.category, project (required), project.start_date, project.end_date. Import tasks from Task Management (not here).";

/** Bare aliases that used to map to task fields in the old project+task bundle CSV. */
const PROJECT_CSV_BARE_TASK_ALIASES = new Set(["due_date", "amount", "repeat", "taskdue"]);

function isRejectedProjectImportHeader(header) {
  const h = String(header || "");
  if (!h) return false;
  if (h === "task" || h.startsWith("task.") || h.startsWith("task_")) return true;
  if (h === "subtask" || h.startsWith("subtask.") || h.startsWith("subtask_")) return true;
  return PROJECT_CSV_BARE_TASK_ALIASES.has(h);
}

function Project() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useAuth();
  const [projectGroups, setProjectGroups] = useState({ regular: [], templates: [], systemTemplates: [] });
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [assets, setAssets] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [exporting, setExporting] = useState(false);

  const {
    selectedIds,
    setSelectedIds,
    deleting,
    setDeleting,
    importing,
    setImporting,
    importPanelExpanded,
    setImportPanelExpanded,
    searchTerm,
    setSearchTerm,
    sortConfig,
    bannerError,
    setBannerError,
    viewMode,
    setViewMode,
    confirmDialog,
    setConfirmDialog,
    closeConfirmDialog,
    handleSort,
    handleSelectAll,
    handleSelectOne,
  } = useEntityListChrome({ initialSort: { key: null, direction: "asc" }, initialViewMode: "card" });

  const navViewFromUrl = (searchParams.get("navView") || "").trim().toLowerCase();
  const navDateFromUrl = (searchParams.get("navDate") || "").trim();
  const statusFromUrl = (searchParams.get("status") || "").trim().toLowerCase();
  const [statusFilter, setStatusFilter] = useState(() => {
    if (statusFromUrl === "open" || statusFromUrl === "closed" || statusFromUrl === "all") {
      return statusFromUrl;
    }
    return "open";
  }); // 'all' | 'open' | 'closed'
  const [activeProjectTab, setActiveProjectTab] = useState("regular");
  const [templateToCreate, setTemplateToCreate] = useState(null);
  const isSystemTemplateTab = activeProjectTab === "system";
  const currentObjs =
    activeProjectTab === "template"
      ? projectGroups.templates
      : isSystemTemplateTab
        ? projectGroups.systemTemplates
        : projectGroups.regular;
  const timeNav = useGlobalTimeNavigator({
    initialView: ["day", "week", "month", "future", "all"].includes(navViewFromUrl) ? navViewFromUrl : "all",
    initialFocusDateYmd: navDateFromUrl || undefined,
  });

  useEffect(() => {
    // Default Open when URL has no status (or unrecognized). Explicit status=all|open|closed still wins.
    if (statusFromUrl === "open" || statusFromUrl === "closed" || statusFromUrl === "all") {
      setStatusFilter(statusFromUrl);
    } else {
      setStatusFilter("open");
    }
    if (["day", "week", "month", "future", "all"].includes(navViewFromUrl)) timeNav.setView(navViewFromUrl);
    if (navDateFromUrl) timeNav.setFocusDateYmd(navDateFromUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- URL-driven initial filters
  }, []);

  useEffect(() => {
    loadAssetsAndVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  useEffect(() => {
    getDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load project tabs once on mount
  }, []);

  function normalizeStatusLikeTask(value) {
    return value && String(value).trim() === "Closed" ? "Closed" : "Open";
  }

  function loadAssetsAndVendors() {
    if (!account_id) return;
    apiFetch(serverUrl + "/listAssets", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((r) => r.json())
      .then((data) => setAssets(Array.isArray(data) ? data : []))
      .catch(() => setAssets([]));

    apiFetch(serverUrl + "/listVendors", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((r) => r.json())
      .then((data) => setVendors(Array.isArray(data) ? data : []))
      .catch(() => setVendors([]));
  }

  function getDocument() {
    if (!account_id) return;
    const fetchProjectGroup = (template_filter) =>
      apiFetch(serverUrl + "/getProject", {
        method: "POST",
        body: JSON.stringify({ account_id, template_filter }),
      }).then(async (resp) => {
        const data = await resp.json().catch(() => null);
        if (!resp.ok) throw new Error(data?.message || `Could not load ${template_filter} projects`);
        return Array.isArray(data) ? data : [];
      });

    Promise.all([
      fetchProjectGroup("regular"),
      fetchProjectGroup("template"),
      fetchSystemTemplateProjects(),
    ])
      .then(([regular, templates, systemTemplates]) =>
        setProjectGroups({ regular, templates, systemTemplates })
      )
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function goToTasksForProject(project) {
    if (!project?._id) return;
    const params = new URLSearchParams();
    params.set("projectId", String(project._id));
    navigate("/Task?" + params.toString());
  }

  function goToAiProjectBuilderForProject(project) {
    navigateToAiProjectBuilderForExistingProject(navigate, {
      _id: project._id,
      name: project.name,
      start_date: project.start_date,
      end_date: project.end_date,
    });
  }

  function getFilteredAndSortedData() {
    const searchLower = (searchTerm || "").toLowerCase().trim();
    const searchFiltered = (currentObjs || []).filter((obj) => {
      if (!searchLower) return true;
      return (
        (obj.name && String(obj.name).toLowerCase().includes(searchLower)) ||
        (obj.category && String(obj.category).toLowerCase().includes(searchLower)) ||
        (obj.status && String(obj.status).toLowerCase().includes(searchLower)) ||
        (obj.notes && String(obj.notes).toLowerCase().includes(searchLower))
      );
    });
    const bounds = timeNav.bounds;
    const rangeStart = bounds.startInclusive ? dayjs(bounds.startInclusive) : null;
    const rangeEnd = bounds.endInclusive ? dayjs(bounds.endInclusive) : null;
    const navFiltered = searchFiltered.filter((p) => {
      const st = (p?.status || "Open").toLowerCase();
      if (statusFilter === "open" && st !== "open") return false;
      if (statusFilter === "closed" && st !== "closed") return false;
      if (timeNav.view === "all") return true;
      const startRaw = p?.start_date ? String(p.start_date).slice(0, 10) : "";
      const endRaw = p?.end_date ? String(p.end_date).slice(0, 10) : "";
      const start = startRaw ? dayjs(startRaw) : null;
      const end = endRaw ? dayjs(endRaw) : null;
      const a = (start && start.isValid()) ? start.startOf("day") : (end && end.isValid() ? end.startOf("day") : null);
      const b = (end && end.isValid()) ? end.endOf("day") : (start && start.isValid() ? start.endOf("day") : null);
      if (!a || !b) return false;
      if (timeNav.view === "future") {
        // Future horizon = starts on/after rangeStart (not overlap)
        if (rangeStart && a.isBefore(rangeStart)) return false;
      } else {
        if (rangeStart && b.isBefore(rangeStart)) return false;
        if (rangeEnd && a.isAfter(rangeEnd)) return false;
      }
      return true;
    });
    return sortRowsByConfig(navFiltered, sortConfig);
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData());
  }

  async function confirmDeleteProject(currentObj) {
    if (!currentObj?._id) return;
    const name = (currentObj.name || "this project").trim() || "this project";
    const displayLabel = name.length > 100 ? `${name.slice(0, 100)}…` : name;
    let impactSummary = formatDeleteProjectImpactSummary(null);
    try {
      const impact = await fetchDeleteProjectImpact({ account_id, _id: currentObj._id });
      impactSummary = formatDeleteProjectImpactSummary(impact);
    } catch {
      impactSummary = formatDeleteProjectImpactSummary(null);
    }
    setConfirmDialog({
      open: true,
      title: "Delete project",
      message: `Delete "${displayLabel}"?\n\n${impactSummary}\n\nThis cannot be undone.`,
      onConfirm: () => {
        deleteDocument(currentObj);
      },
    });
  }

  function deleteDocument(currentObj) {
    const { _id: id } = currentObj;
    apiFetch(serverUrl + "/deleteProject", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: id }),
    })
      .then((response) => response.text())
      .then((data) => {
        setConfirmDialog({ open: true, title: "Project", message: data, mode: "alert", onConfirm: null });
        const newSelectedIds = new Set(selectedIds);
        newSelectedIds.delete(id);
        setSelectedIds(newSelectedIds);
        getDocument();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function duplicateDocument(currentObj) {
    const duplicateName = buildDuplicateLabel(currentObj?.name, { fallback: "Project" });
    apiFetch(serverUrl + "/createProject", {
      method: "POST",
      body: JSON.stringify({
        account_id,
        name: duplicateName,
        is_template: currentObj?.is_template === true,
        start_date: currentObj?.start_date || undefined,
        end_date: currentObj?.end_date || undefined,
        asset_id: currentObj?.asset_id || "",
        vendor_id: currentObj?.vendor_id || "",
        status: normalizeStatusLikeTask(currentObj?.status),
        notes: (currentObj?.notes != null ? String(currentObj.notes) : "").trim(),
      }),
    })
      .then(async (response) => {
        const data = await response.text();
        if (!response.ok) throw new Error(data || "Failed to duplicate project");
        let created = null;
        try {
          created = JSON.parse(data);
        } catch {
          created = null;
        }
        const newId = created && created._id != null ? String(created._id) : "";
        const openCreated = () => {
          closeConfirmDialog();
          if (newId) goToTasksForProject({ _id: newId });
        };
        setConfirmDialog({
          open: true,
          title: "Project duplicated",
          message:
            newId ? (
              <DuplicateSuccessMessage linkLabel={duplicateName.trim() || "Project"} onOpenDetail={openCreated} footer={null} />
            ) : (
              `Created "${duplicateName}".`
            ),
          mode: "alert",
          onConfirm: null,
        });
        getDocument();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function openCreateFromTemplate(currentObj) {
    setTemplateToCreate({
      project: currentObj,
      scope: isSystemTemplateTab ? "system" : "account",
      initialName: buildDuplicateLabel(currentObj?.name, { fallback: "Project" }),
      initialStartDate: dayjs().format("YYYY-MM-DD"),
    });
  }

  async function submitCreateFromTemplate(values) {
    const selected = templateToCreate;
    if (!selected?.project?._id) throw new Error("No template selected.");
    const data = await createProjectFromTemplate({
      account_id,
      template_project_id: selected.project._id,
      template_scope: selected.scope,
      name: values.name,
      start_date: values.start_date,
      created_by: userInfo?._id || "",
    });
    const newId = data?.project?._id != null ? String(data.project._id) : "";
    if (!newId) throw new Error("Project was created, but the response did not include its ID.");
    setTemplateToCreate(null);
    goToTasksForProject({ _id: newId });
  }

  function renderDeleteProjectAction(project) {
    if (isSystemTemplateTab) return null;
    if (activeProjectTab === "template") {
      return (
        <button
          type="button"
          onClick={() => void confirmDeleteProject(project)}
          className="btn btn-sm btn-danger"
          title="Delete template"
        >
          Delete
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => void confirmDeleteProject(project)}
        className="btn-link-danger"
        style={iconBtnStyle}
        title="Delete"
      >
        <IconDelete size={18} />
      </button>
    );
  }

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: "Delete projects", message: "Please select at least one record to delete", mode: "alert", onConfirm: null });
      return;
    }
    const idsToDelete = Array.from(selectedVisibleIds);
    let impactSummary = formatDeleteProjectImpactSummary(null);
    try {
      const impact = await fetchDeleteProjectImpact({ account_id, _ids: idsToDelete });
      impactSummary = formatDeleteProjectImpactSummary(impact);
    } catch {
      impactSummary = formatDeleteProjectImpactSummary(null);
    }
    setConfirmDialog({
      open: true,
      title: "Delete projects",
      message: `You are about to delete ${idsToDelete.length} selected project(s).\n\n${impactSummary}\n\nThis cannot be undone.`,
      onConfirm: async () => {
        setDeleting(true);
        const { successCount, errorCount, errors } = await bulkSequentialDelete(
          idsToDelete,
          async (_id) => {
            const response = await apiFetch(serverUrl + "/deleteProject", {
              method: "POST",
              body: JSON.stringify({ account_id, _id }),
            });
            const data = await response.text();
            return { ok: response.ok, body: data };
          },
          getMessageForFetchError
        );
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: formatBatchSummaryMessage("Delete completed!", successCount, errorCount, errors),
          mode: "alert",
          onConfirm: null,
        });
        setSelectedIds(new Set());
        getDocument();
        setDeleting(false);
      },
    });
  }

  function parseCSV(csvText) {
    const { headers, rows } = parseCsvTextToNormalizedTable(csvText);

    const rejected = headers.filter(isRejectedProjectImportHeader);
    if (rejected.length > 0) {
      throw new Error(
        `Project import does not accept task columns (${rejected.join(", ")}). Import tasks from Task Management → Import CSV.`
      );
    }

    const assetIdx = findCsvColumnIndex(headers, ["asset", "asset_name"]);
    const assetCatIdx = findCsvColumnIndex(headers, ["asset.category", "asset_category", "assetcat", "asset_cat"]);
    const projectIdx = findCsvColumnIndex(headers, ["project", "project_name", "name"]);
    const projectStartIdx = findCsvColumnIndex(headers, [
      "project.start_date",
      "project_start_date",
      "projectstart",
      "start_date",
    ]);
    const projectEndIdx = findCsvColumnIndex(headers, [
      "project.end_date",
      "project_end_date",
      "projectend",
      "end_date",
    ]);

    if (projectIdx === -1) throw new Error("CSV must contain column: project");

    return rows.map((values, i) => ({
      asset: csvCellAt(values, assetIdx),
      asset_category: csvCellAt(values, assetCatIdx),
      project: csvCellAt(values, projectIdx),
      project_start_date: csvCellAt(values, projectStartIdx),
      project_end_date: csvCellAt(values, projectEndIdx),
      _rowNum: i + 2,
    }));
  }

  /** Validate dates and build API rows in one pass (no second date re-parse). */
  function buildProjectImportRows(records) {
    const errors = [];
    const cleaned = [];
    for (const r of records) {
      const project = String(r.project || "").trim();
      if (!project) continue;

      const rowErrs = [];
      let project_start_date = "";
      let project_end_date = "";
      const startRaw = String(r.project_start_date || "").trim();
      const endRaw = String(r.project_end_date || "").trim();
      if (startRaw) {
        const x = parseCsvDateToIso(startRaw);
        if (!x.ok) rowErrs.push(`Invalid project start date: ${x.error}`);
        else project_start_date = x.iso || "";
      }
      if (endRaw) {
        const x = parseCsvDateToIso(endRaw);
        if (!x.ok) rowErrs.push(`Invalid project end date: ${x.error}`);
        else project_end_date = x.iso || "";
      }
      if (rowErrs.length) {
        errors.push(`Row ${r._rowNum}: ${rowErrs.join("; ")}`);
        continue;
      }
      cleaned.push({
        asset: String(r.asset || "").trim(),
        asset_category: String(r.asset_category || "").trim(),
        project,
        project_start_date,
        project_end_date,
        _rowNum: r._rowNum,
      });
    }
    return { errors, cleaned };
  }

  async function importFromCSV(file) {
    const text = await file.text();
    await importFromCsvText(text);
  }

  async function importFromCsvText(text) {
    setImporting(true);
    try {
      const records = parseCSV(text);
      if (records.length === 0) {
        setConfirmDialog({ open: true, title: "Import", message: "No valid records found in CSV file", mode: "alert", onConfirm: null });
        return;
      }

      const { errors: fieldErrors, cleaned } = buildProjectImportRows(records);
      if (fieldErrors.length > 0) {
        setConfirmDialog({
          open: true,
          title: "Import",
          mode: "alert",
          onConfirm: null,
          message:
          `Fix CSV values before importing (dates: YYYY-MM-DD or M/D/YYYY with a 4-digit year).\n\n${fieldErrors
            .slice(0, 15)
            .join("\n")}${fieldErrors.length > 15 ? `\n… and ${fieldErrors.length - 15} more` : ""}`
        });
        return;
      }
      if (cleaned.length === 0) {
        setConfirmDialog({ open: true, title: "Import", message: "No valid records found in CSV file", mode: "alert", onConfirm: null });
        return;
      }

      const resp = await apiFetch(serverUrl + "/importProjectBundle", {
        method: "POST",
        body: JSON.stringify({
          account_id,
          created_by: userInfo?._id || "",
          rows: cleaned,
        }),
      });
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        const msg = data?.message || "Import failed.";
        const errs = Array.isArray(data?.errors) ? data.errors : [];
        setConfirmDialog({
          open: true,
          title: "Import",
          message: [msg, ...errs.slice(0, 10)].join("\n"),
          mode: "alert",
          onConfirm: null,
        });
        return;
      }

      const summary = data?.imported || {};
      const errList = Array.isArray(data?.errors) ? data.errors : [];
      let message = `Import completed!\nRows processed: ${summary.rowsProcessed || 0}\nAssets touched: ${summary.assetsTouched || 0}\nProjects touched: ${summary.projectsTouched || 0}`;
      if (errList.length > 0) message += `\n\nRow issues (${errList.length}):\n` + errList.slice(0, 10).join("\n");
      setConfirmDialog({
        open: true,
        title: "Import completed",
        message,
        mode: "alert",
        onConfirm: null,
      });
      getDocument();
    } catch (error) {
      const msg = getMessageForFetchError(error, "Error importing CSV.");
      if (error instanceof Error && error.message && msg === error.message) {
        setConfirmDialog({ open: true, title: "Import", message: msg, mode: "alert", onConfirm: null });
      } else {
        setBannerError(msg);
      }
    } finally {
      setImporting(false);
    }
  }

  function handleFileChange(e) {
    attachValidatedCsvFileImport(e, importFromCSV, (msg) => {
      setConfirmDialog({ open: true, title: "Import", message: msg, mode: "alert", onConfirm: null });
    });
  }

  function downloadSampleCSV() {
    downloadCsvTextFile("project-sample.csv", [
      PROJECT_CSV_HEADER,
      "Placeholder,Investment Home,Vacate Tenant,2026-01-01,2026-01-31",
      "Placeholder,Investment Home,Barbarosa Vacate/Repair,2026-02-01,2026-02-28",
      "Benz 450,Car/SUV,Recurring Work,2026-01-01,2026-12-31",
    ]);
  }

  async function exportSelectedProjectsCsv() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Export",
        message: "Select at least one project to export.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setExporting(true);
    try {
      const project_ids = [...selectedVisibleIds].map((id) => String(id));
      const resp = await apiFetch(serverUrl + "/exportProjectCsv", {
        method: "POST",
        body: JSON.stringify({
          account_id,
          project_ids,
          user_id: userInfo?._id || "",
          role: userInfo?.role || "",
        }),
      });
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        setBannerError(data?.message || "Export failed.");
        return;
      }
      if (!data?.success || typeof data.csv !== "string") {
        setBannerError("Export failed: invalid response.");
        return;
      }
      setBannerError("");
      const filename = typeof data.filename === "string" && data.filename.trim() ? data.filename.trim() : "project-export.csv";
      downloadCsvTextFromString(filename, data.csv);
    } catch (err) {
      setBannerError(getMessageForFetchError(err, "Error exporting CSV."));
    } finally {
      setExporting(false);
    }
  }

  const filteredRows = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  return (
    <>
      <ProjectFromTemplateModal
        open={!!templateToCreate}
        templateName={templateToCreate?.project?.name || ""}
        initialName={templateToCreate?.initialName || ""}
        initialStartDate={templateToCreate?.initialStartDate || ""}
        onCancel={() => setTemplateToCreate(null)}
        onCreate={submitCreateFromTemplate}
      />
      <EntityPageScaffold
      title="Project Management"
      subtitle="Manage projects, scheduling, assets, vendors, and notes."
      bannerError={bannerError}
      onDismissBanner={() => setBannerError("")}
      confirmDialog={confirmDialog}
      onConfirmModal={() => {
        const fn = confirmDialog.onConfirm;
        closeConfirmDialog();
        if (fn) fn();
      }}
      onCancelModal={closeConfirmDialog}
      pageTopAfterModal={
        <>
          <div className="tabs-bar tabs-bar-underline project-type-tabs">
            <div className="tabs-list" role="tablist" aria-label="Project type">
              <button
                type="button"
                role="tab"
                aria-selected={activeProjectTab === "regular"}
                className={`tab-btn ${activeProjectTab === "regular" ? "active" : ""}`}
                onClick={() => {
                  setSelectedIds(new Set());
                  setActiveProjectTab("regular");
                }}
              >
                <TabLabelWithCount label="Active Projects" count={projectGroups.regular.length} />
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeProjectTab === "template"}
                className={`tab-btn ${activeProjectTab === "template" ? "active" : ""}`}
                onClick={() => {
                  setSelectedIds(new Set());
                  setActiveProjectTab("template");
                }}
              >
                <TabLabelWithCount label="My Templates" count={projectGroups.templates.length} />
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isSystemTemplateTab}
                className={`tab-btn ${isSystemTemplateTab ? "active" : ""}`}
                onClick={() => {
                  setSelectedIds(new Set());
                  setImportPanelExpanded(false);
                  setActiveProjectTab("system");
                }}
              >
                <TabLabelWithCount label="System Templates" count={projectGroups.systemTemplates.length} />
              </button>
            </div>
          </div>

          {!isSystemTemplateTab && (
            <ImportCsvPanel
              expanded={importPanelExpanded}
              fileInputId="project-csv-import"
              importing={importing}
              columnHint={PROJECT_CSV_COLUMN_HINT}
              onDownloadSample={downloadSampleCSV}
              onFileChange={handleFileChange}
              onImportText={importFromCsvText}
            />
          )}

          <CrudListToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search name, category, status, notes…"
            onClearSearch={() => setSearchTerm("")}
            middleSlot={
              <>
                <OpenClosedFilterDropdown value={statusFilter} onChange={setStatusFilter} label="Status" />
                <span className="toolbar-divider" />
              </>
            }
            showCsvImport={!isSystemTemplateTab}
            showNew={!isSystemTemplateTab}
            showDelete={!isSystemTemplateTab}
            importPanelExpanded={importPanelExpanded}
            onToggleImport={() => setImportPanelExpanded(!importPanelExpanded)}
            extraActions={!isSystemTemplateTab ? (
              <button
                type="button"
                className="btn btn-md btn-primary-outline"
                disabled={selectedVisibleCount === 0 || exporting}
                onClick={() => void exportSelectedProjectsCsv()}
              >
                {exporting ? "Exporting…" : `Export CSV${selectedCountSuffix}`}
              </button>
            ) : null}
            onNew={() => {
              navigate("/AIProjectBuilder");
              setImportPanelExpanded(false);
            }}
            onDeleteSelected={deleteSelected}
            deleteDisabled={selectedVisibleCount === 0 || deleting}
            deleteCountSuffix={selectedCountSuffix}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            secondRowForViewAndTrailing
            trailingSlot={
              <GlobalTimeNavigatorBar
                variant="inline"
                title={timeNav.title}
                view={timeNav.view}
                onViewChange={timeNav.setView}
                onToday={timeNav.goToday}
                onPrev={timeNav.movePrev}
                onNext={timeNav.moveNext}
              />
            }
          />
        </>
      }
      list={
        <div className="table-scroll">
          {viewMode === "table" && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th className="text-center">
                      {!isSystemTemplateTab && (
                        <input
                          type="checkbox"
                          checked={filteredRows.length > 0 && filteredRows.every((obj) => selectedIds.has(obj._id))}
                          onChange={(e) => handleSelectAll(e, getFilteredAndSortedData)}
                          disabled={filteredRows.length === 0}
                        />
                      )}
                    </th>
                    <th className="sortable" onClick={() => handleSort("name")}>Name</th>
                    <th className="sortable" onClick={() => handleSort("is_template")}>Project Type</th>
                    <th className="sortable" onClick={() => handleSort("category")}>Category</th>
                    <th>Asset</th>
                    <th>Vendor</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Overall Progress</th>
                    <th className="sortable" onClick={() => handleSort("status")}>Status</th>
                    <th>Notes</th>
                    <TableActionColumnHeader />
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((p) => {
                    const asset = (assets || []).find((a) => String(a._id) === String(p.asset_id));
                    const vendor = (vendors || []).find((v) => String(v._id) === String(p.vendor_id));
                    const taskTotal = Number(p.taskTotal || 0) || 0;
                    const taskClosed = Number(p.taskClosed || 0) || 0;
                    const progressPct = taskTotal > 0 ? Math.round((taskClosed / taskTotal) * 100) : 0;
                    return (
                      <tr key={p._id}>
                        <td className="text-center">
                          {!isSystemTemplateTab && (
                            <input type="checkbox" checked={selectedIds.has(p._id)} onChange={(e) => handleSelectOne(p._id, e.target.checked)} />
                          )}
                        </td>
                        <td>
                          {isSystemTemplateTab ? (
                            p.name || "—"
                          ) : (
                            <button type="button" className="btn-link block" onClick={() => goToTasksForProject(p)}>
                              {p.name || "—"}
                            </button>
                          )}
                        </td>
                        <td>{isSystemTemplateTab ? "System Template" : (p.is_template === true ? "Template" : "Regular Project")}</td>
                        <td>{p.category || "—"}</td>
                        <td>{asset ? getAssetLabel(asset) : "—"}</td>
                        <td>{vendor ? (vendor.name + (vendor.type ? ` (${vendor.type})` : "")) : "—"}</td>
                        <td>{formatDateCell(p.start_date)}</td>
                        <td>{formatDateCell(p.end_date)}</td>
                        <td>
                          {taskTotal > 0 ? (
                            <div
                              className="project-header-progress"
                              style={{ maxWidth: 220 }}
                              title={getProjectProgressTooltipTitle(taskClosed, taskTotal, progressPct)}
                            >
                              <div className="project-header-progress__bar">
                                <div
                                  className="project-header-progress__fill"
                                  style={{
                                    width: `${progressPct}%`,
                                    backgroundColor: getProjectProgressFillColor(taskClosed, taskTotal, progressPct),
                                  }}
                                />
                              </div>
                              <div className="project-header-progress__text">{`${progressPct}% (${taskClosed}/${taskTotal})`}</div>
                            </div>
                          ) : (
                            "No tasks"
                          )}
                        </td>
                        <td>{normalizeStatusLikeTask(p.status)}</td>
                        <td className="table-cell-notes">{p.notes ? p.notes : "—"}</td>
                        <td className="text-center">
                          <RowActionIcons>
                            {activeProjectTab === "regular" && (
                              <button
                                type="button"
                                onClick={() => goToAiProjectBuilderForProject(p)}
                                className="btn-link"
                                style={iconBtnStyle}
                                title="Add tasks with AI"
                              >
                                <IconAiAddTasks size={20} />
                              </button>
                            )}
                            {activeProjectTab === "regular" ? (
                              <button
                                type="button"
                                onClick={() => duplicateDocument(p)}
                                className="btn-link"
                                style={iconBtnStyle}
                                title="Duplicate"
                              >
                                <IconDuplicate size={18} />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => openCreateFromTemplate(p)}
                                className="btn btn-sm btn-primary"
                                title="Create an active project from this template"
                              >
                                + Create Project
                              </button>
                            )}
                            {renderDeleteProjectAction(p)}
                          </RowActionIcons>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {viewMode === "card" && (
            <div className="cards-wrapper">
              {filteredRows.map((p) => {
                const asset = (assets || []).find((a) => String(a._id) === String(p.asset_id));
                const vendor = (vendors || []).find((v) => String(v._id) === String(p.vendor_id));
                const taskTotal = Number(p.taskTotal || 0) || 0;
                const taskClosed = Number(p.taskClosed || 0) || 0;
                const progressPct = taskTotal > 0 ? Math.round((taskClosed / taskTotal) * 100) : 0;
                return (
                  <div key={p._id} className="card">
                    <div className="card-header">
                      {!isSystemTemplateTab && (
                        <input type="checkbox" checked={selectedIds.has(p._id)} onChange={(e) => handleSelectOne(p._id, e.target.checked)} />
                      )}
                      <div className="card-header-content">
                        {isSystemTemplateTab ? (
                          <div className="card-title">{p.name || "—"}</div>
                        ) : (
                          <button type="button" className="btn-link card-title" onClick={() => goToTasksForProject(p)}>
                            {p.name || "—"}
                          </button>
                        )}
                        <div className="panel-hint">
                          {(isSystemTemplateTab ? "System Template" : (p.is_template === true ? "Template" : "Regular Project")) + " · " + (p.category || "—") + " · " + normalizeStatusLikeTask(p.status)}
                        </div>
                        <div className="panel-hint">
                          {"Start: " + formatDateCell(p.start_date) + " · End: " + formatDateCell(p.end_date)}
                        </div>
                        <div className="panel-hint" style={{ marginTop: '6px' }}>
                          <div
                            className="project-header-progress"
                            style={{ maxWidth: 260 }}
                            title={getProjectProgressTooltipTitle(taskClosed, taskTotal, progressPct)}
                          >
                            <div className="project-header-progress__bar">
                              <div
                                className="project-header-progress__fill"
                                style={{
                                  width: `${progressPct}%`,
                                  backgroundColor: getProjectProgressFillColor(taskClosed, taskTotal, progressPct),
                                }}
                              />
                            </div>
                            <div className="project-header-progress__text">
                              {taskTotal > 0 ? `Overall Progress: ${progressPct}% (${taskClosed}/${taskTotal})` : 'Overall Progress: No tasks'}
                            </div>
                          </div>
                        </div>
                        {asset && <div className="panel-hint">{getAssetLabel(asset)}</div>}
                        {vendor && <div className="panel-hint">{vendor.name + (vendor.type ? ` (${vendor.type})` : "")}</div>}
                      </div>
                    </div>
                    <div className="card-footer">
                      {activeProjectTab === "regular" ? (
                        <RowActionIcons variant="inline">
                          <button
                            type="button"
                            onClick={() => goToAiProjectBuilderForProject(p)}
                            className="btn-link"
                            style={iconBtnStyle}
                            title="Add tasks with AI"
                          >
                            <IconAiAddTasks size={20} />
                          </button>
                          <button
                            type="button"
                            onClick={() => duplicateDocument(p)}
                            className="btn-link"
                            style={iconBtnStyle}
                            title="Duplicate"
                          >
                            <IconDuplicate size={18} />
                          </button>
                          {renderDeleteProjectAction(p)}
                        </RowActionIcons>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => openCreateFromTemplate(p)}
                            className="btn btn-sm btn-primary"
                            title="Create an active project from this template"
                          >
                            + Create Project
                          </button>
                          {renderDeleteProjectAction(p)}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      }
      />
    </>
  );
}

export default Project;
